import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { toast } from 'sonner';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import CustomSpinner from '../../../loader/CustomSpinner';
import { setSelectedView } from '../../../redux/pickupProfileSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { PickupService } from '../../../services/pickup/pickupService';
import { PickupApiResponse } from '../../../type/entities/pickup-type';

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.pickups.profile.info,
        permission: {
            group: 'pickup',
            action: 'get',
        },
    },
    {
        label: 'Productos',
        path: menuRoutes.pickups.profile.products,
        permission: {
            group: 'pickup',
            action: 'get',
        },
    },
    {
        label: 'Historial de estados',
        path: menuRoutes.pickups.profile.statusHistory,
        permission: {
            group: 'pickup_status_log',
            action: 'list',
        },
    }
];

export default function PickupProfileLayout({ isLoading }: { isLoading?: boolean }) {

    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.pickupProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();
    const service = new PickupService();

    // DATA FETCHING

    //--------------------------------------------------------------------------------------------------------------
    /**
     *  @EN FETCH PICKUP DATA BY ID
     *  @ES OBTIENE LOS DATOS DE UNA RECOGIDA POR SU ID
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loadingPickup, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getPickupById(id);
        return response.getResponseData() as PickupApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    const formattedDate = data?.pickupDate?.date
        ? new Intl.DateTimeFormat('es-ES', { dateStyle: 'long' }).format(new Date(data.pickupDate.date))
        : '';

    const pageHeader = {
        title: data?.id
            ? `Recogida ${data.headquarters?.name} ( ${formattedDate} )`
            : 'Recogida',
        breadcrumb: [
            { name: 'Recogidas', href: menuRoutes.pickups.path },
            { name: 'Perfil de Recogida' },
        ],
    };

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     *  @EN HANDLES PICKUP DELETION BY ID
     *  @ES MANEJA LA ELIMINACION DE UNA RECOGIDA POR EL ID
     */
    //--------------------------------------------------------------------------------------------------------------    
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deletePickup(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.pickups.path);
                toast.success('Recogida eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.pickups.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <div className='m-auto h-full'><CustomSpinner /></div> : <Outlet />}
        </>
    );
}